// src/components/modals/TermsModal.tsx
import React from 'react';
import { FileText, AlertTriangle, Users, Gavel, X } from 'lucide-react';
import type { ModalProps } from '../../types/components';

export const TermsModal: React.FC<ModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <FileText className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Terms of Service</h1>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <p className="text-gray-600 mb-8">
          Last updated: August 19, 2026
        </p>
        
        <div className="space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Acceptance of Terms</h2>
            <p className="text-gray-700">
              By accessing and using Dairy Translate, you accept and agree to be bound by the terms 
              and provision of this agreement. These Terms of Service apply to all visitors, users, 
              and others who access or use our DC305 translation service.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Use License</h2>
            </div>
            <p className="text-gray-700 mb-4">
              Permission is granted to temporarily access Dairy Translate for personal, 
              non-commercial transitory viewing only. Under this license you may not:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2">
              <li>Modify or copy the materials</li>
              <li>Use the materials for commercial purposes or public display</li>
              <li>Attempt to reverse engineer any software contained on the service</li>
              <li>Remove any copyright or proprietary notations from the materials</li>
            </ul>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Disclaimer</h2>
            </div>
            <p className="text-gray-700 mb-4">
              The materials on Dairy Translate are provided on an 'as is' basis. We make no warranties
              regarding accuracy of AI-generated translations or fitness for particular purposes.
              AI-generated output may contain errors. You are solely responsible for reviewing and
              verifying any generated command before executing it in your DairyComp 305 system. Dairy
              Translate is not affiliated with, endorsed by, or officially connected to DHI Computing
              Service, Inc. or DairyComp 305.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Limitation of Liability</h2>
            <p className="text-gray-700">
              To the fullest extent permitted by law, Dairy Translate and its operator shall not be
              liable for any indirect, incidental, special, or consequential damages, or any loss of
              data, revenue, or profits, arising from your use of or inability to use the service.
              This service is provided as a proof-of-concept without dedicated support or uptime
              guarantees.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Termination</h2>
            <p className="text-gray-700">
              We reserve the right to suspend or terminate your access to the service at any time,
              without notice, for conduct that violates these Terms or is otherwise harmful to other
              users or the service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Changes to These Terms</h2>
            <p className="text-gray-700">
              We may update these Terms of Service from time to time. Continued use of the service
              after changes are posted constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-4">
              <Gavel className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900">Governing Law</h2>
            </div>
            <p className="text-gray-700">
              These terms are governed by the laws of South Dakota, United States.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Contact Information</h2>
            <p className="text-gray-700">
              Questions about these Terms? Contact us at{' '}
              <a href="mailto:dairytranslate@gmail.com" className="text-blue-600 hover:text-blue-700">
                dairytranslate@gmail.com
              </a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};